import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';

import { environment } from '../../environments/environment';

export interface IMyGuestChatLinkResponse {
  token: string | null;
  expiresAt?: string;
}

const CHAT_LINK_URL = `${environment.api}/visitors-area/public/my-guest/chat-link`;

/**
 * Token linku do czatu myGuest. myGuest nie przyjmuje już `agentId`/`guestId` z przeglądarki —
 * tożsamość gościa nadaje jego serwer przy wymianie tokenu na sesję, a token wydaje tylko naszemu
 * backendowi (uwierzytelnionemu wspólnym sekretem). Stąd to dodatkowe wywołanie zamiast złożenia
 * adresu iframe z identyfikatorów.
 *
 * Udany token trzymamy w pamięci: każde mintowanie tworzy po stronie myGuest nowy rekord linku, a
 * token żyje tygodniami, więc nie ma powodu pytać przy każdym otwarciu panelu. Nieudanej próby NIE
 * cache'ujemy — inaczej jeden błąd sieci zablokowałby czat do przeładowania strony.
 */
@Injectable({
  providedIn: 'root',
})
export class MyGuestChatLinkService {
  #token?: string;
  #pending$?: Observable<string | null>;

  constructor(private readonly http: HttpClient) {}

  getToken(): Observable<string | null> {
    if (this.#token) {
      return of(this.#token);
    }
    if (!this.#pending$) {
      this.#pending$ = this.http.get<IMyGuestChatLinkResponse>(CHAT_LINK_URL).pipe(
        map(response => response?.token ?? null),
        tap(token => {
          if (token) {
            this.#token = token;
          }
        }),
        catchError(() => of(null)),
        finalize(() => (this.#pending$ = undefined)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.#pending$;
  }

  /** Czyści cache — po zmianie gościa w tej samej karcie token poprzedniego jest już nieaktualny. */
  reset(): void {
    this.#token = undefined;
    this.#pending$ = undefined;
  }
}
